import React, { useState, useEffect } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { Card, Row, Col, Badge, Button, Spinner, Tab, Tabs } from 'react-bootstrap';
import {
  FiEdit2, FiArrowLeft, FiCalendar, FiUser,
  FiExternalLink, FiFileText, FiClock, FiPaperclip
} from 'react-icons/fi';
import { toast } from 'react-toastify';
import casesService, { Case } from '../../services/cases.service';
import CaseActivityLog from '../../components/cases/CaseActivityLog';
import AttachmentPanel from '../../components/AttachmentPanel';

const CaseDetail: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [caso, setCaso] = useState<Case | null>(null);

  useEffect(() => {
    if (id) loadCase();
  }, [id]);

  const loadCase = async () => {
    try {
      setLoading(true);
      const data = await casesService.getById(Number(id));
      setCaso(data);
    } catch {
      toast.error('Error al cargar el caso');
      navigate('/cases');
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateStr?: string) => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleDateString('es-CO', {
      year: 'numeric', month: 'long', day: 'numeric'
    });
  };

  const getStatusBadge = (status: string) => {
    const variants: Record<string, string> = { active: 'success', suspended: 'warning', closed: 'secondary' };
    const labels: Record<string, string> = { active: 'Activo', suspended: 'Suspendido', closed: 'Cerrado' };
    return <Badge bg={variants[status] || 'secondary'}>{labels[status] || status}</Badge>;
  };

  const getSemaphoreDot = (color?: string) => {
    const variants: Record<string, string> = { green:'success', yellow:'warning', red:'danger', gray:'secondary' };
    return (
      <span
        className={`badge bg-${variants[color || 'gray'] || 'secondary'}`}
        style={{ width: 12, height: 12, borderRadius: '50%', display: 'inline-block' }}
      />
    );
  };

  if (loading) return <div className="text-center py-5"><Spinner animation="border" variant="primary" /></div>;
  if (!caso) return null;

  return (
    <div className="case-detail">
      {/* Header */}
      <div className="d-flex justify-content-between align-items-start mb-4">
        <div>
          <div className="d-flex align-items-center gap-2 mb-2">
            <Button variant="light" size="sm" onClick={() => navigate('/cases')}>
              <FiArrowLeft />
            </Button>
            <h4 className="mb-0">
              {getSemaphoreDot(caso.semaphore_color)} {caso.case_number}
            </h4>
            {getStatusBadge(caso.status)}
          </div>
          <p className="text-muted mb-0">
            {caso.client_name} · {caso.process_type_name || 'Sin tipo'}
          </p>
        </div>
        <div className="d-flex gap-2">
          {caso.expediente_url && (
            <Button variant="outline-info" href={caso.expediente_url} target="_blank" as="a">
              <FiExternalLink className="me-1" /> Ver Expediente
            </Button>
          )}
          <Link to={`/cases/${id}/edit`} className="btn btn-primary">
            <FiEdit2 className="me-1" /> Editar
          </Link>
        </div>
      </div>

      <Tabs defaultActiveKey="info" className="mb-4">

        {/* TAB: INFORMACIÓN */}
        <Tab eventKey="info" title={<><FiFileText className="me-1" />Información</>}>
          <Row>
            <Col lg={8}>
              <Card className="mb-4">
                <Card.Header><h6 className="mb-0">Datos del Caso</h6></Card.Header>
                <Card.Body>
                  <Row>
                    <Col md={6}>
                      <div className="mb-3">
                        <small className="text-muted d-block">Demandante</small>
                        <strong>{caso.plaintiff || '-'}</strong>
                      </div>
                    </Col>
                    <Col md={6}>
                      <div className="mb-3">
                        <small className="text-muted d-block">Demandado</small>
                        <strong>{caso.defendant || '-'}</strong>
                      </div>
                    </Col>
                    <Col md={6}>
                      <div className="mb-3">
                        <small className="text-muted d-block">Juzgado / Entidad</small>
                        <strong>{caso.court_name || '-'}</strong>
                      </div>
                    </Col>
                    <Col md={6}>
                      <div className="mb-3">
                        <small className="text-muted d-block">Jurisdicción</small>
                        <strong>{caso.jurisdiction_entity || '-'}</strong>
                      </div>
                    </Col>
                    <Col md={6}>
                      <div className="mb-3">
                        <small className="text-muted d-block">Juez</small>
                        <strong>{caso.judge_name || '-'}</strong>
                      </div>
                    </Col>
                    <Col md={6}>
                      <div className="mb-3">
                        <small className="text-muted d-block">Estado del Proceso</small>
                        <strong>{caso.process_status || '-'}</strong>
                      </div>
                    </Col>
                    {caso.summary && (
                      <Col md={12}>
                        <div className="mb-0">
                          <small className="text-muted d-block">Resumen</small>
                          <span>{caso.summary}</span>
                        </div>
                      </Col>
                    )}
                  </Row>
                  {caso.expediente_url && (
                    <div className="mt-3 p-3 bg-light rounded">
                      <small className="text-muted d-block mb-1">Link del Expediente</small>
                      <a href={caso.expediente_url} target="_blank" rel="noopener noreferrer">
                        <FiExternalLink className="me-1" />{caso.expediente_url}
                      </a>
                    </div>
                  )}
                </Card.Body>
              </Card>

              {caso.next_hearing_date && (
                <Card className="mb-4">
                  <Card.Header><h6 className="mb-0"><FiCalendar className="me-2" />Próxima Audiencia</h6></Card.Header>
                  <Card.Body>
                    <Row>
                      <Col md={4}><small className="text-muted d-block">Fecha</small><strong>{formatDate(caso.next_hearing_date)}</strong></Col>
                      <Col md={4}><small className="text-muted d-block">Hora</small><strong>{caso.next_hearing_time || '-'}</strong></Col>
                      <Col md={4}><small className="text-muted d-block">Reprogramada</small><strong>{caso.hearing_rescheduled ? 'Sí' : 'No'}</strong></Col>
                    </Row>
                  </Card.Body>
                </Card>
              )}

              {caso.last_action_notes && (
                <Card className="mb-4">
                  <Card.Header><h6 className="mb-0"><FiClock className="me-2" />Última Actuación</h6></Card.Header>
                  <Card.Body><p className="mb-0">{caso.last_action_notes}</p></Card.Body>
                </Card>
              )}
            </Col>

            <Col lg={4}>
              <Card className="mb-4">
                <Card.Header><h6 className="mb-0"><FiCalendar className="me-2" />Fechas</h6></Card.Header>
                <Card.Body>
                  <div className="mb-3"><small className="text-muted d-block">Radicación</small><strong>{formatDate(caso.filing_date)}</strong></div>
                  <div className="mb-3"><small className="text-muted d-block">Registro</small><strong>{formatDate(caso.registration_date)}</strong></div>
                  <div><small className="text-muted d-block">Creado en sistema</small><strong>{formatDate(caso.created_at)}</strong></div>
                </Card.Body>
              </Card>

              <Card className="mb-4">
                <Card.Header><h6 className="mb-0"><FiUser className="me-2" />Asignación</h6></Card.Header>
                <Card.Body>
                  <small className="text-muted d-block">Abogado Encargado</small>
                  <strong>{caso.assigned_lawyer_name || 'Sin asignar'}</strong>
                </Card.Body>
              </Card>
            </Col>
          </Row>
        </Tab>

        {/* TAB: DOCUMENTOS */}
        <Tab eventKey="documentos" title={<><FiPaperclip className="me-1" />Documentos</>}>
          <AttachmentPanel entityType="case" entityId={Number(id)} />
        </Tab>

        {/* TAB: BITÁCORA */}
        <Tab eventKey="bitacora" title="Bitácora de Seguimiento">
          <CaseActivityLog caseId={Number(id)} />
        </Tab>

      </Tabs>
    </div>
  );
};

export default CaseDetail;
